import type { RouteRecordRaw } from "vue-router";
import type { PortalSettings } from "@ai-platform/contracts";
import { decideAccess, type Session } from "../session/access.ts";
import { portalRoutes } from "./routes.ts";

export type NavItem = { name: string; title: string };
export type NavGroup = { id: "products" | "account" | "staff"; title: string | null; items: NavItem[] };

/**
 * The navigation for a session, as the design prototype's `renderNav` (ADR-150 point 1): every
 * route with a `nav` group that the session may open, in the order of the route table. On the
 * customers' side the overview and the products without a heading, signed in "Euer Konto" below
 * them; staff see their area first and the products as "Ansicht wie ein Gast" (C-09); a test
 * account with both keeps its customer side first (07 §2.3). A guest sees nothing while the guest
 * view is off (ADR-133 point 3). "Übersicht" is one item: the customer's
 * own overview where the session may open it, else the overview of all products (point 3).
 * Nothing here decides a permission, it reads the same `decideAccess` the guard uses.
 */
export function navigationFor(
  session: Session,
  settings: PortalSettings,
  routes: readonly RouteRecordRaw[] = portalRoutes,
): NavGroup[] {
  const itemsOf = (group: NavGroup["id"]): NavItem[] =>
    routes
      .filter((route) => route.meta?.nav === group && decideAccess(route.meta.access, session, settings) === "allow")
      .map((route) => ({ name: String(route.name), title: route.meta?.title ?? "" }));

  const allowed = itemsOf("products");
  const products = allowed.some((item) => item.name === "home")
    ? allowed.filter((item) => item.name !== "catalog")
    : allowed;
  const account = itemsOf("account");
  const staff = itemsOf("staff");

  const accountGroup: NavGroup[] = account.length > 0 ? [{ id: "account", title: "Euer Konto", items: account }] : [];
  const staffGroup: NavGroup[] = staff.length > 0 ? [{ id: "staff", title: "Mitarbeiterbereich", items: staff }] : [];
  if (products.length === 0) return [...accountGroup, ...staffGroup];
  // a test account with both keeps its customer side first; staff alone see the products as guests do
  if (staff.length === 0 || session?.customer) {
    return [{ id: "products", title: null, items: products }, ...accountGroup, ...staffGroup];
  }
  return [...staffGroup, { id: "products", title: "Ansicht wie ein Gast", items: products }];
}
